import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { AdminRequestRow, DocumentType } from "../lib/api";
import { fullLAK, num } from "../lib/format";
import { formatAgoLo } from "../lib/requestStatus";
import { CheckCircle, ChevronLeft, ChevronRight, FileText, Inbox, PenLine, X } from "../components/icons";
import { canSignDocuments, hasPerm, PERM } from "../lib/permissions";

/**
 * ລໍເຊັນ ແລະ ປະທັບຕາ — จอทำงานของผู้เซ็น (ຫົວໜ້າ/ຮອງຫົວໜ້າຕາແສງ) ตาม mockup v2 `work_sign.js`
 *
 * ซ้าย = คิวที่อนุมัติแล้ว (เก่าสุดก่อน) + เลือกหลายใบเพื่อลงนามทีเดียว · กลาง = ภาพใบที่จะออกจริง
 * (`GET …/sign-preview/url` — PDF ฉบับเดียวกับที่จะออก ยังไม่ประทับลายเซ็น จอวางรูปลายเซ็นทับให้ดู)
 * · ขวา = เลขที่ที่จะได้ + ลงนาม/ส่งคืน
 *
 * - เลขที่: server จองตอนเซ็นเอง (ไม่ส่ง `document_number`) — เลขในพรีวิวเป็นค่าประมาณ **ห้ามส่งกลับ** (CLAUDE.md)
 * - ยืนยันด้วย PIN (`POST /v1/me/pin/verify`) แทน OTP ของ mockup — แบบเดียวกับแอปตาแสง; server ไม่บังคับตอน …/sign
 * - ไม่ทำจาก mockup: ກວດອັດຕະໂນມັດ 3 ຂໍ້ (ยังไม่มีการตรวจกับสำมะโนครัวจริง) และสวิตช์ ຮັກສາການແທນ (ยังไม่มีการมอบสิทธิ์)
 * - สัญญาซื้อขาย (`x-layout: sale_contract`) ต้องติ๊กว่าอีกฝ่ายเซ็นต่อหน้าแล้ว จึงไม่อยู่ในการลงนามหลายใบ
 */

const RETURN_CHIPS = ["ເອກະສານບໍ່ຄົບ", "ຂໍ້ມູນບໍ່ຕົງກັບສຳມະໂນຄົວ", "ບັດໝົດອາຍຸ", "ຮູບບໍ່ຊັດ"];

async function fetchApproved(): Promise<AdminRequestRow[]> {
  const { data, error, response } = await api.GET("/v1/admin/requests", {
    params: { query: { limit: 200, status: ["approved"] } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  // เวลาที่เข้าคิวลงนาม ≈ updated_at (การอนุมัติคือการแก้ครั้งสุดท้ายของคำร้องที่ approved)
  return data.items.slice().sort((a, b) => a.updated_at.localeCompare(b.updated_at));
}

async function fetchIssued(): Promise<AdminRequestRow[]> {
  const { data, error, response } = await api.GET("/v1/admin/requests", {
    params: { query: { limit: 200, status: ["issued"] } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

async function fetchDocumentTypes(): Promise<DocumentType[]> {
  const { data, error, response } = await api.GET("/v1/document-types", { params: { query: { limit: 100 } } });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

const isSaleContract = (t: DocumentType | undefined) =>
  (t?.form_schema as Record<string, unknown> | undefined)?.["x-layout"] === "sale_contract";

const hoursSince = (iso: string, now: number) => Math.max(0, (now - new Date(iso).getTime()) / 3_600_000);

function waitLabel(hours: number): string {
  if (hours < 1) return "ບໍ່ຮອດ 1 ຊົ່ວໂມງ";
  if (hours < 24) return `${num(Math.round(hours))} ຊົ່ວໂມງ`;
  return `${(hours / 24).toLocaleString("lo-LA", { maximumFractionDigits: 1 })} ມື້`;
}

export function SignDeskPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canSign = canSignDocuments();
  const canReturn = hasPerm(PERM.returnForChanges);

  const queue = useQuery({ queryKey: ["requests", "sign-desk"], queryFn: fetchApproved, enabled: canSign });
  const issued = useQuery({ queryKey: ["requests", "sign-desk", "issued"], queryFn: fetchIssued, enabled: canSign });
  const types = useQuery({ queryKey: ["document-types"], queryFn: fetchDocumentTypes });
  const now = queue.dataUpdatedAt;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // opts = ตำแหน่ง/ตัวเลือกที่ผู้เซ็นตั้งในพรีวิว (ใบเดียว) · ลงนามหลายใบใช้ค่าตั้งต้นของแต่ละใบ
  const [confirm, setConfirm] = useState<{ ids: string[]; opts?: SignOptions } | null>(null);
  const [returning, setReturning] = useState<AdminRequestRow | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const rows = queue.data ?? [];
  const typeOf = (r: AdminRequestRow) => types.data?.find((t) => t.id === r.document_type_id);
  const currentID = params.get("id");
  const current = rows.find((r) => r.id === currentID) ?? rows[0] ?? null;
  const idx = current ? rows.indexOf(current) : -1;

  const pick = (id: string) => {
    setParams({ id }, { replace: true });
  };

  if (!canSign) {
    return (
      <div className="alert-error">ໜ້ານີ້ສຳລັບຜູ້ມີສິດລົງນາມເອກະສານ (ຫົວໜ້າ / ຮອງຫົວໜ້າຕາແສງ) ເທົ່ານັ້ນ</div>
    );
  }

  // ---- KPI: นับจากคิวจริงเท่านั้น — "ເຊັນມື້ນີ້" นับใบ issued ที่ signed_at เป็นวันนี้ (เวลาเครื่อง)
  const waits = rows.map((r) => hoursSince(r.updated_at, now));
  const avgWait = waits.length ? waits.reduce((a, b) => a + b, 0) / waits.length : null;
  const overDay = waits.filter((h) => h > 24).length;
  const today = new Date(now || 0).toDateString();
  const issuedRows = issued.data ?? [];
  const signedToday = issuedRows.filter((r) => r.signed_at && new Date(r.signed_at).toDateString() === today).length;
  const signedTodayLabel = issued.isPending
    ? "…"
    : issuedRows.length >= 200 && signedToday === issuedRows.length
      ? "200+"
      : num(signedToday);

  const batchable = rows.filter((r) => !isSaleContract(typeOf(r)));
  const selN = rows.filter((r) => selected.has(r.id)).length;
  const allSelected = batchable.length > 0 && batchable.every((r) => selected.has(r.id));

  function afterDone(ids: string[], message: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((i) => next.delete(i));
      return next;
    });
    // ใบถัดไปขึ้นมาแทนที่ตำแหน่งเดิม
    const rest = rows.filter((r) => !ids.includes(r.id));
    const nextRow = rest[Math.min(Math.max(idx, 0), rest.length - 1)];
    setParams(nextRow ? { id: nextRow.id } : {}, { replace: true });
    setFlash(message);
    void qc.invalidateQueries({ queryKey: ["requests"] });
  }

  return (
    <div>
      <header className="rd-header">
        <div>
          <h1 className="page-header__title">ລໍເຊັນ ແລະ ປະທັບຕາ</h1>
          <p className="page-header__subtitle">
            ຄຳຮ້ອງທີ່ຜູ້ກວດເອກະສານກວດແລ້ວ — ເຊັນແລ້ວແກ້ເນື້ອໃນບໍ່ໄດ້ ຖ້າຜິດຕ້ອງອອກສະບັບໃໝ່
          </p>
        </div>
        <Link to="/requests" className="btn-outline btn-sm">
          <Inbox size={14} /> ຄິວຄຳຮ້ອງທັງໝົດ
        </Link>
      </header>

      <div className="kpi-grid">
        <article className={rows.length > 0 ? "kpi kpi--warn" : "kpi"}>
          <div className="lbl">ລໍລົງນາມ</div>
          <div className="val num">{queue.isPending ? "…" : num(rows.length)}</div>
          <div className="delta">{rows[0] ? `ເກົ່າສຸດ ${formatAgoLo(rows[0].updated_at)}` : "ບໍ່ມີລາຍການຄ້າງ"}</div>
        </article>
        <article className="kpi">
          <div className="lbl">ເຊັນມື້ນີ້</div>
          <div className="val num">{signedTodayLabel}</div>
          <div className="delta">ທຸກຜູ້ເຊັນໃນເຂດຂອງທ່ານ</div>
        </article>
        <article className="kpi">
          <div className="lbl">ເວລາລໍສະເລ່ຍ (ທີ່ຄ້າງຢູ່)</div>
          <div className="val num">{avgWait == null ? "—" : waitLabel(avgWait)}</div>
          <div className="delta">ນັບຈາກຜ່ານການກວດ · ເປົ້າໝາຍ ≤ 1 ວັນ</div>
        </article>
        <article className={overDay > 0 ? "kpi kpi--warn" : "kpi"}>
          <div className="lbl">ລໍເກີນ 1 ວັນ</div>
          <div className="val num">{queue.isPending ? "…" : num(overDay)}</div>
          <div className="delta">ນັບເປັນຊົ່ວໂມງ ບໍ່ຫັກວັນພັກ</div>
        </article>
      </div>

      {flash ? (
        <div className="rd-note rd-note--success" role="status" style={{ marginBottom: "var(--space-4)" }}>
          <CheckCircle size={14} /> {flash}
        </div>
      ) : null}
      {queue.isError ? <div className="alert-error">ໂຫຼດຄິວບໍ່ສຳເລັດ: {queue.error.message}</div> : null}

      {!queue.isPending && rows.length === 0 ? (
        <section className="card sd-empty">
          <CheckCircle size={32} />
          <h2 className="section-title">ບໍ່ມີລາຍການລໍລົງນາມ</h2>
          <p className="caption">ຄຳຮ້ອງທີ່ຜູ້ກວດເອກະສານກວດຜ່ານແລ້ວ ຈະຂຶ້ນຢູ່ບ່ອນນີ້</p>
          <Link to="/requests" className="btn-outline btn-sm">
            ເບິ່ງຄິວຄຳຮ້ອງ
          </Link>
        </section>
      ) : (
        <div className="sd-wrap">
          {/* ---- ຄິວລໍລົງນາມ ---- */}
          <div>
            <div className="row" style={{ marginBottom: "var(--space-2)", gap: "var(--space-2)" }}>
              <strong className="body-sm">ຄິວລໍລົງນາມ</strong>
              <span className="caption">ເກົ່າສຸດກ່ອນ · {num(rows.length)} ລາຍການ</span>
            </div>
            <div className="sd-list">
              {rows.map((r) => {
                const t = typeOf(r);
                const sale = isSaleContract(t);
                const hours = hoursSince(r.updated_at, now);
                return (
                  <div
                    key={r.id}
                    className={r.id === current?.id ? "sd-item is-active" : "sd-item"}
                    role="button"
                    tabIndex={0}
                    onClick={() => pick(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        pick(r.id);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`ເລືອກ ${t?.name_lo ?? ""}`}
                      checked={selected.has(r.id)}
                      disabled={sale}
                      title={sale ? "ສັນຍາຕ້ອງຢືນຢັນວ່າອີກຝ່າຍເຊັນຕໍ່ໜ້າ — ລົງນາມທີລະໃບ" : undefined}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (on) next.add(r.id);
                          else next.delete(r.id);
                          return next;
                        });
                      }}
                    />
                    <div className="sd-item__body">
                      <div className="body-sm" style={{ fontWeight: 600 }}>
                        {t?.name_lo ?? "—"} <span className="caption num">{t?.code}</span>
                      </div>
                      <div className="caption">
                        {r.requester_name || "—"}
                        {r.village_name_lo ? ` · ບ້ານ${r.village_name_lo}` : ""}
                      </div>
                      <div className="row" style={{ gap: "var(--space-2)", marginTop: "var(--space-1)" }}>
                        <span className={hours > 24 ? "sd-chip sd-chip--warn" : "sd-chip"}>ລໍ {waitLabel(hours)}</span>
                        {sale ? <span className="sd-chip">ສັນຍາ — ເຊັນທີລະໃບ</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="sd-bar">
              <div className="row" style={{ justifyContent: "space-between" }}>
                <strong className="body-sm">ເລືອກແລ້ວ {num(selN)} ລາຍການ</strong>
                <button
                  type="button"
                  className="link-action"
                  disabled={batchable.length === 0}
                  onClick={() => setSelected(allSelected ? new Set() : new Set(batchable.map((r) => r.id)))}
                >
                  {allSelected ? "ຍົກເລີກການເລືອກ" : "ເລືອກທັງໝົດ"}
                </button>
              </div>
              <button
                type="button"
                className="btn-secondary rd-block"
                disabled={selN === 0}
                onClick={() => setConfirm({ ids: rows.filter((r) => selected.has(r.id)).map((r) => r.id) })}
              >
                <PenLine size={16} /> ລົງນາມທີ່ເລືອກ ({num(selN)})
              </button>
              <p className="caption">
                ຕ້ອງໃສ່ PIN ຢືນຢັນ · ເລກທີອອກຕາມລຳດັບທີ່ເຊັນ · ລາຍເຊັນ ແລະ QR ວາງຕາມຕຳແໜ່ງຕັ້ງຕົ້ນຂອງແຕ່ລະເອກະສານ
              </p>
            </div>
          </div>

          {/* ---- ເອກະສານທີ່ເລືອກ + ການດຳເນີນການ ---- */}
          {current ? (
            <div>
              <div className="row sd-toolbar">
                <strong className="body-sm">ເອກະສານທີ່ເລືອກ</strong>
                <span className="caption">
                  ລາຍການ {num(idx + 1)} ຈາກ {num(rows.length)}
                </span>
                <span className="status-badge status-badge--pending">ຕົວຢ່າງ · ຍັງບໍ່ໄດ້ລົງນາມ</span>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn-outline btn-sm"
                  aria-label="ລາຍການກ່ອນໜ້າ"
                  disabled={idx <= 0}
                  onClick={() => rows[idx - 1] && pick(rows[idx - 1]!.id)}
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  className="btn-outline btn-sm"
                  aria-label="ລາຍການຖັດໄປ"
                  disabled={idx >= rows.length - 1}
                  onClick={() => rows[idx + 1] && pick(rows[idx + 1]!.id)}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
              <SignPanel
                key={current.id}
                row={current}
                type={typeOf(current)}
                canReturn={canReturn}
                onSign={(opts) => setConfirm({ ids: [current.id], opts })}
                onReturn={() => setReturning(current)}
                onOpenDetail={() => navigate(`/requests/${current.id}?from=sign`)}
              />
            </div>
          ) : null}
        </div>
      )}

      {confirm ? (
        <SignConfirmModal
          rows={rows.filter((r) => confirm.ids.includes(r.id))}
          opts={confirm.opts}
          typeOf={typeOf}
          onClose={() => setConfirm(null)}
          onDone={(signed, failed) => {
            setConfirm(null);
            afterDone(
              signed.map((s) => s.id),
              failed
                ? `ລົງນາມແລ້ວ ${num(signed.length)} ລາຍການ · ບໍ່ສຳເລັດ ${num(failed)} ລາຍການ (ຍັງຢູ່ໃນຄິວ)`
                : signed.length === 1
                  ? `ລົງນາມແລ້ວ — ເລກທີ ${signed[0]?.number ?? "—"} · ແຈ້ງຜູ້ຂໍແລ້ວ`
                  : `ລົງນາມແລ້ວ ${num(signed.length)} ລາຍການ — ເລກທີ ${signed[0]?.number ?? ""} ຫາ ${signed[signed.length - 1]?.number ?? ""}`,
            );
          }}
        />
      ) : null}
      {returning ? (
        <ReturnModal
          row={returning}
          type={typeOf(returning)}
          onClose={() => setReturning(null)}
          onDone={() => {
            const id = returning.id;
            setReturning(null);
            afterDone([id], "ສົ່ງຄືນແລ້ວ — ແຈ້ງຜູ້ຂໍທາງແອັບ ພ້ອມເຫດຜົນ");
          }}
        />
      ) : null}
    </div>
  );
}

type Placement = { page: number; x: number; y: number; width: number; height: number };
type PageSize = { width: number; height: number };

/** ตัวเลือกตอนลงนามที่ผู้เซ็นตั้งในจอพรีวิว — ส่งตรงไป `POST …/sign` */
interface SignOptions {
  signature_placement?: Placement;
  qr_placement?: Placement;
  hide_qr: boolean;
  hide_signer_name: boolean;
}

// QR: server บีบขนาดให้อยู่ 40–110 pt (กล้องอ่านได้) — จอบีบแบบเดียวกันให้เห็นตรงกับใบจริง
const QR_MIN_PT = 40;
const QR_MAX_PT = 110;

/**
 * ภาพทุกหน้าเรียงลงมา กว้างเท่ากัน — แปลงกรอบ (สัดส่วนของหน้า) ⇄ กรอบบนภาพรวม (สัดส่วนของภาพทั้งก้อน)
 */
function pageRatios(pages: PageSize[]) {
  const r = pages.map((pg) => pg.height / pg.width);
  return { r, total: r.reduce((a, b) => a + b, 0) };
}
function toBox(pl: Placement, pages: PageSize[]) {
  const { r, total } = pageRatios(pages);
  const before = r.slice(0, pl.page - 1).reduce((a, b) => a + b, 0);
  const pr = r[pl.page - 1] ?? 1;
  return { left: pl.x, top: (before + pl.y * pr) / total, width: pl.width, height: (pl.height * pr) / total };
}
function fromBox(box: { left: number; top: number; width: number; height: number }, pages: PageSize[]): Placement {
  const { r, total } = pageRatios(pages);
  const abs = box.top * total;
  let before = 0;
  let page = 1;
  for (let i = 0; i < r.length; i++) {
    const ri = r[i] ?? 1;
    page = i + 1;
    if (abs < before + ri || i === r.length - 1) break;
    before += ri;
  }
  const pr = r[page - 1] ?? 1;
  const height = Math.min((box.height * total) / pr, 1);
  const y = Math.min(Math.max((abs - before) / pr, 0), 1 - height);
  const width = Math.min(box.width, 1);
  return { page, x: Math.min(Math.max(box.left, 0), 1 - width), y, width, height };
}

// บรรทัดชื่อผู้เซ็นใต้ลายเซ็น (pt) — ต้องตรงกับ signerNameStamp* ใน apps/api documentrequest/signing.go
const SIGNER_NAME_HEIGHT = 16;
const SIGNER_NAME_GAP = 2;
const SIGNER_NAME_MIN_WIDTH = 160;
const SIGNER_NAME_MARGIN = 4;
// บรรทัดวันที่ใต้ QR (pt) — ต้องตรงกับ qrDate* ใน apps/api documentrequest/document_verify.go
const QR_DATE_HEIGHT = 12;
const QR_DATE_GAP = 1;
const QR_DATE_MIN_WIDTH = 60;
const QR_DATE_MARGIN = 4;

/** วันที่วันนี้ตามเวลาลาว (DD/MM/YYYY) — server ประทับวันที่เซ็นแบบเดียวกันใต้ QR */
function todayLao(): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Vientiane",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());
}

/** วันที่เซ็นใต้ QR — ย้ายตาม QR (สูตรเดียวกับ qrDateField ของ API) · QR จัตุรัสในหน่วย pt ใช้ความกว้าง */
function QRDateLabel({ qr, pages }: { qr: Placement; pages: PageSize[] }) {
  const pg = pages[qr.page - 1];
  if (!pg) return null;
  const size = qr.width * pg.width;
  const q = { x: qr.x * pg.width, y: qr.y * pg.height };
  const w = Math.min(Math.max(size, QR_DATE_MIN_WIDTH), pg.width - 2 * QR_DATE_MARGIN);
  const x = Math.min(Math.max(q.x + size / 2 - w / 2, QR_DATE_MARGIN), pg.width - QR_DATE_MARGIN - w);
  const y = Math.min(q.y + size + QR_DATE_GAP, pg.height - QR_DATE_MARGIN - QR_DATE_HEIGHT);
  const box = toBox(
    { page: qr.page, x: x / pg.width, y: y / pg.height, width: w / pg.width, height: QR_DATE_HEIGHT / pg.height },
    pages,
  );
  return (
    <span
      className="sd-stamp-name num"
      aria-hidden="true"
      style={{
        left: `${String(box.left * 100)}%`,
        top: `${String(box.top * 100)}%`,
        width: `${String(box.width * 100)}%`,
        height: `${String(box.height * 100)}%`,
        fontSize: `${String((QR_DATE_HEIGHT / 1.5 / pg.width) * 100)}cqw`,
      }}
    >
      {todayLao()}
    </span>
  );
}

/**
 * ชื่อผู้เซ็นใต้ลายเซ็น — server ประทับคู่ลายเซ็นตอนเซ็น (ย้ายตามกรอบลายเซ็น) ภาพพรีวิวจึงไม่มีชื่อในช่องลงชื่อ
 * วาดตามสูตรเดียวกับ signerNameField ของ API · ขนาดตัวอักษรเป็น cqw ของหน้า (.sd-doc__page เป็น container)
 */
function SignerNameLabel({ sig, pages, name }: { sig: Placement; pages: PageSize[]; name: string }) {
  const pg = pages[sig.page - 1];
  if (!pg) return null;
  const s = { x: sig.x * pg.width, y: sig.y * pg.height, w: sig.width * pg.width, h: sig.height * pg.height };
  const w = Math.min(Math.max(s.w, SIGNER_NAME_MIN_WIDTH), pg.width - 2 * SIGNER_NAME_MARGIN);
  const x = Math.min(Math.max(s.x + s.w / 2 - w / 2, SIGNER_NAME_MARGIN), pg.width - SIGNER_NAME_MARGIN - w);
  const y = Math.min(s.y + s.h + SIGNER_NAME_GAP, pg.height - SIGNER_NAME_MARGIN - SIGNER_NAME_HEIGHT);
  const box = toBox(
    { page: sig.page, x: x / pg.width, y: y / pg.height, width: w / pg.width, height: SIGNER_NAME_HEIGHT / pg.height },
    pages,
  );
  return (
    <span
      className="sd-stamp-name"
      aria-hidden="true"
      style={{
        left: `${String(box.left * 100)}%`,
        top: `${String(box.top * 100)}%`,
        width: `${String(box.width * 100)}%`,
        height: `${String(box.height * 100)}%`,
        fontSize: `${String((SIGNER_NAME_HEIGHT / 1.5 / pg.width) * 100)}cqw`,
      }}
    >
      {name}
    </span>
  );
}

/**
 * กรอบลากได้บนภาพเอกสาร — ลากตัวกรอบ = ย้าย · ลากมุมขวาล่าง = ย่อ/ขยาย (คงสัดส่วน)
 * `square` = QR (จัตุรัสในหน่วย pt ของหน้า) · ค่าเป็นสัดส่วนของหน้า ตรงกับ SignaturePlacement ของ API
 */
function DraggableStamp({
  value,
  pages,
  square,
  minWidth,
  maxWidth,
  src,
  label,
  onChange,
}: {
  value: Placement;
  pages: PageSize[];
  square: boolean;
  minWidth: (pl: Placement) => number;
  maxWidth: (pl: Placement) => number;
  src: string;
  label: string;
  onChange: (pl: Placement) => void;
}) {
  const box = toBox(value, pages);

  function start(e: React.PointerEvent<HTMLElement>, mode: "move" | "resize") {
    e.preventDefault();
    e.stopPropagation();
    const host = (e.currentTarget.closest(".sd-doc__page") as HTMLElement | null)?.getBoundingClientRect();
    if (!host) return;
    const target = e.currentTarget;
    target.setPointerCapture(e.pointerId);
    const sx = e.clientX;
    const sy = e.clientY;
    const b0 = box;
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - sx) / host.width;
      const dy = (ev.clientY - sy) / host.height;
      if (mode === "move") {
        const left = Math.min(Math.max(b0.left + dx, 0), 1 - b0.width);
        const top = Math.min(Math.max(b0.top + dy, 0), 1 - b0.height);
        onChange(fromBox({ ...b0, left, top }, pages));
      } else {
        const cur = fromBox(b0, pages);
        const w = Math.min(Math.max(b0.width + dx, minWidth(cur)), maxWidth(cur), 1 - b0.left);
        // คงสัดส่วนกรอบ — QR จัตุรัสในหน่วยพิกเซลของภาพ (= จัตุรัสใน pt เพราะทุกหน้ากว้างเท่าภาพ)
        const h = square ? (w * host.width) / host.height : (b0.height * w) / b0.width;
        onChange(fromBox({ left: b0.left, top: Math.min(b0.top, 1 - h), width: w, height: h }, pages));
      }
    };
    const end = () => {
      target.removeEventListener("pointermove", move);
      target.removeEventListener("pointerup", end);
      target.removeEventListener("pointercancel", end);
    };
    target.addEventListener("pointermove", move);
    target.addEventListener("pointerup", end);
    target.addEventListener("pointercancel", end);
  }

  // แป้นลูกศรย้ายทีละน้อย (Shift = ทีละมาก) — ใช้ได้โดยไม่ต้องมีเมาส์
  function onKey(e: React.KeyboardEvent<HTMLElement>) {
    const step = e.shiftKey ? 0.02 : 0.005;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    const left = Math.min(Math.max(box.left + (d[0] ?? 0), 0), 1 - box.width);
    const top = Math.min(Math.max(box.top + (d[1] ?? 0) / 3, 0), 1 - box.height);
    onChange(fromBox({ ...box, left, top }, pages));
  }

  return (
    <div
      className="sd-stamp"
      role="button"
      tabIndex={0}
      aria-label={`${label} — ລາກເພື່ອຍ້າຍ ຫຼື ໃຊ້ປຸ່ມລູກສອນ`}
      style={{
        left: `${String(box.left * 100)}%`,
        top: `${String(box.top * 100)}%`,
        width: `${String(box.width * 100)}%`,
        height: `${String(box.height * 100)}%`,
      }}
      onPointerDown={(e) => start(e, "move")}
      onKeyDown={onKey}
    >
      <img src={src} alt="" draggable={false} />
      <span className="sd-stamp__tag">{label}</span>
      <span
        className="sd-stamp__resize"
        aria-hidden="true"
        onPointerDown={(e) => start(e, "resize")}
      />
    </div>
  );
}

/** ที่มาของตำแหน่งลายเซ็นตั้งต้น (`placement_source` ของ sign-preview) — ให้ผู้เซ็นรู้ว่าต้องตรวจแค่ไหน */
const PLACEMENT_SOURCE_LO: Record<string, string> = {
  saved: "ຕາມຕຳແໜ່ງທີ່ບັນທຶກໄວ້ສຳລັບເອກະສານນີ້",
  template_field: "ຕາມຊ່ອງລາຍເຊັນທີ່ວາງໄວ້ໃນແບບຟອມ",
  template_slot: "ລະບົບວາງໃຫ້ອັດຕະໂນມັດ ຕາມຊ່ອງລົງຊື່ໃນແມ່ແບບ",
  fallback: "ຫາຊ່ອງລົງຊື່ໃນເອກະສານນີ້ບໍ່ພົບ — ກວດ ແລະ ລາກໃຫ້ຕົງກ່ອນເຊັນ",
};

/** ภาพใบที่จะออก + การ์ดลงนามของใบที่เลือก */
function SignPanel({
  row,
  type,
  canReturn,
  onSign,
  onReturn,
  onOpenDetail,
}: {
  row: AdminRequestRow;
  type: DocumentType | undefined;
  canReturn: boolean;
  onSign: (opts: SignOptions) => void;
  onReturn: () => void;
  onOpenDetail: () => void;
}) {
  // พรีวิวช้า (เปิด chromium จริง) และ URL หมดอายุได้ — ไม่ cache ข้ามการเปิดหน้า
  const preview = useQuery({
    queryKey: ["requests", "sign-preview", row.id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/v1/admin/requests/{id}/sign-preview/url", {
        params: { path: { id: row.id } },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data;
    },
    staleTime: 60_000,
    gcTime: 60_000,
    retry: false,
  });
  const p = preview.data;
  // null = ยังไม่ได้ลาก ใช้กรอบตั้งต้นของพรีวิว (ไม่ส่งไป server = server ใช้ค่าตั้งต้นเดียวกัน)
  const [sig, setSig] = useState<Placement | null>(null);
  const [qr, setQr] = useState<Placement | null>(null);
  const [showQR, setShowQR] = useState(true);
  const [hideName, setHideName] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);
  const qc = useQueryClient();

  const sigPl = sig ?? p?.placement ?? null;
  const qrPl = qr ?? p?.qr_placement ?? null;
  const hasQR = !!(p?.qr_image && p.qr_placement);
  const pageOf = (pl: Placement) => p?.pages[pl.page - 1] ?? { width: 595, height: 842 };
  const opts: SignOptions = {
    ...(sig ? { signature_placement: sig } : {}),
    ...(qr && showQR ? { qr_placement: qr } : {}),
    hide_qr: hasQR && !showQR,
    hide_signer_name: hideName,
  };

  // บันทึก/ล้างตำแหน่งของชนิดเอกสารนี้ (มีผลทุกใบของชนิดนี้ทุกเขต) — แล้วโหลดพรีวิวทุกใบใหม่ให้เห็นค่าใหม่
  const afterPlacementChange = () => {
    setSig(null);
    setQr(null);
    void qc.invalidateQueries({ queryKey: ["requests", "sign-preview"] });
  };
  const savePlacement = useMutation({
    mutationFn: async () => {
      if (!p || !sigPl) return;
      const { error, response } = await api.PUT("/v1/admin/requests/{id}/sign-placement", {
        params: { path: { id: row.id } },
        body: {
          signature_placement: sigPl,
          ...(hasQR && showQR && qrPl ? { qr_placement: qrPl } : {}),
          pages: p.pages.length,
        },
        headers: authHeaders(),
      });
      if (error) throw new Error(describeError(error, response.status));
    },
    onSuccess: afterPlacementChange,
  });
  const clearPlacement = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.DELETE("/v1/admin/requests/{id}/sign-placement", {
        params: { path: { id: row.id } },
        headers: authHeaders(),
      });
      if (error) throw new Error(describeError(error, response.status));
    },
    onSuccess: afterPlacementChange,
  });
  const dragged = sig !== null || qr !== null;
  const source = p?.placement_source ?? "";
  const typeName = type?.name_lo ?? "ເອກະສານນີ້";

  // ภาพสุดท้ายแบบ WYSIWYG — server ประทับลายเซ็น/ตรา/QR ลง PDF จริงแล้วแปลงเป็นภาพ (ไม่เซ็น ไม่จองเลข)
  const final = useMutation({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/v1/admin/requests/{id}/sign-preview/url", {
        params: { path: { id: row.id } },
        body: {
          signature_placement: sigPl ?? undefined,
          ...(showQR && qrPl ? { qr_placement: qrPl } : {}),
          // ส่งเฉพาะตอนเลือกไม่ใส่ QR — API รุ่นก่อนมีช่องนี้ปฏิเสธฟิลด์ที่ไม่รู้จัก (400) · cast เพราะ
          // openapi-typescript ทำช่องที่มี default เป็น required ทั้งที่ server ถือว่าไม่ส่ง = false
          ...((hasQR && !showQR ? { hide_qr: true } : {}) as { hide_qr: boolean }),
          hide_signer_name: hideName,
        },
        headers: authHeaders(),
      });
      if (error || !data) throw new Error(describeError(error, response.status));
      return data.url;
    },
    onSuccess: () => setFinalOpen(true),
  });

  return (
    <div className="sd-main">
      <div className="sd-doc">
        {preview.isPending ? (
          <p className="caption sd-doc__msg">ກຳລັງສ້າງຕົວຢ່າງເອກະສານ… (ອາດໃຊ້ເວລາຫຼາຍວິນາທີ)</p>
        ) : preview.isError ? (
          <div className="sd-doc__msg">
            <p className="field-error">ສ້າງຕົວຢ່າງບໍ່ສຳເລັດ: {preview.error.message}</p>
            <button type="button" className="btn-outline btn-sm" onClick={() => void preview.refetch()}>
              ລອງໃໝ່
            </button>
          </div>
        ) : p ? (
          <>
            <p className="caption sd-doc__hint">
              ລາກລາຍເຊັນ{hasQR && showQR ? " ແລະ QR" : ""} ໄປວາງບ່ອນທີ່ຕ້ອງການ · ລາກມຸມຂວາລຸ່ມເພື່ອຍໍ້/ຂະຫຍາຍ
            </p>
            <div className="sd-doc__page">
              <img src={p.url} alt={`ຕົວຢ່າງ ${type?.name_lo ?? "ເອກະສານ"}`} draggable={false} />
              {p.signature_url && sigPl ? (
                <DraggableStamp
                  value={sigPl}
                  pages={p.pages}
                  square={false}
                  minWidth={() => 0.06}
                  maxWidth={() => 0.6}
                  src={p.signature_url.url}
                  label="ລາຍເຊັນ"
                  onChange={setSig}
                />
              ) : null}
              {p.signature_url && sigPl && !hideName && p.signer_name ? (
                <SignerNameLabel sig={sigPl} pages={p.pages} name={p.signer_name} />
              ) : null}
              {hasQR && showQR && qrPl && p.qr_image ? (
                <DraggableStamp
                  value={qrPl}
                  pages={p.pages}
                  square
                  minWidth={(pl) => QR_MIN_PT / pageOf(pl).width}
                  maxWidth={(pl) => QR_MAX_PT / pageOf(pl).width}
                  src={p.qr_image}
                  label="QR"
                  onChange={setQr}
                />
              ) : null}
              {hasQR && showQR && qrPl && p.qr_image ? <QRDateLabel qr={qrPl} pages={p.pages} /> : null}
            </div>
          </>
        ) : null}
      </div>

      <div className="stack">
        <section className="card" style={{ padding: "var(--space-4)" }}>
          <h2 className="section-title">ການດຳເນີນການ</h2>
          <div className="stack" style={{ marginTop: "var(--space-3)" }}>
            <div className="rd-number">
              <div className="caption">ເລກທີ່ຈະອອກເມື່ອລົງນາມ</div>
              <div className="rd-number__value num">{p?.document_number || (preview.isPending ? "…" : "—")}</div>
              <div className="caption">ລະບົບອອກເລກຕອນເຊັນ — ອາດຂະຫຍັບ ຖ້າມີຄົນເຊັນໃບອື່ນກ່ອນ</div>
            </div>
            {p && !p.signature_url ? (
              <div className="alert-warning" role="status">
                ທ່ານຍັງບໍ່ໄດ້ຕັ້ງຮູບລາຍເຊັນ — ເອກະສານຈະມີແຕ່ຊື່ຜູ້ລົງນາມ ແລະ ບໍ່ມີ QR (ຕັ້ງໄດ້ໃນແອັບຕາແສງ)
              </div>
            ) : null}
            {p?.signature_url ? (
              <div className="sd-options">
                <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
                  <input
                    type="checkbox"
                    checked={hasQR && showQR}
                    disabled={!hasQR}
                    onChange={(e) => setShowQR(e.target.checked)}
                  />
                  ໃສ່ QR ກວດສອບເອກະສານ
                </label>
                {!hasQR ? <p className="caption">ລະບົບນີ້ຍັງບໍ່ໄດ້ຕັ້ງທີ່ຢູ່ເວັບສາທາລະນະ — ບໍ່ມີ QR</p> : null}
                <label className="row body-sm" style={{ gap: "var(--space-2)" }}>
                  <input type="checkbox" checked={hideName} onChange={(e) => setHideName(e.target.checked)} />
                  ບໍ່ພິມຊື່ຜູ້ລົງນາມ (ມີແຕ່ລາຍເຊັນ / ຕາ)
                </label>
              </div>
            ) : null}
            {p?.signature_url ? (
              <div className="sd-options">
                <strong className="body-sm">ຕຳແໜ່ງລາຍເຊັນ</strong>
                {dragged ? (
                  <div className="rd-note">ທ່ານລາກຍ້າຍແລ້ວ — ໃຊ້ກັບໃບນີ້ໃບດຽວ ຈົນກວ່າຈະບັນທຶກ</div>
                ) : (
                  <div className={source === "fallback" ? "rd-note rd-note--warn" : "rd-note rd-note--success"}>
                    {PLACEMENT_SOURCE_LO[source] ?? "ຕຳແໜ່ງຕັ້ງຕົ້ນ"}
                  </div>
                )}
                {dragged ? (
                  <>
                    <button
                      type="button"
                      className="btn-secondary btn-sm rd-block"
                      disabled={savePlacement.isPending}
                      onClick={() => savePlacement.mutate()}
                    >
                      {savePlacement.isPending ? "ກຳລັງບັນທຶກ…" : `ບັນທຶກເປັນຕຳແໜ່ງຂອງ ${typeName}`}
                    </button>
                    <p className="caption">ໃຊ້ກັບທຸກໃບຂອງ {typeName} ທຸກເຂດ ລວມທັງການລົງນາມຫຼາຍລາຍການ ແລະ ແອັບຕາແສງ</p>
                    <button
                      type="button"
                      className="link-action"
                      onClick={() => {
                        setSig(null);
                        setQr(null);
                      }}
                    >
                      ກັບຄືນຕຳແໜ່ງຕັ້ງຕົ້ນ
                    </button>
                  </>
                ) : source === "saved" ? (
                  <button
                    type="button"
                    className="link-action"
                    disabled={clearPlacement.isPending}
                    onClick={() => clearPlacement.mutate()}
                  >
                    ລຶບຕຳແໜ່ງທີ່ບັນທຶກ — ກັບໄປໃຊ້ຕຳແໜ່ງອັດຕະໂນມັດຈາກແມ່ແບບ
                  </button>
                ) : null}
                {savePlacement.isError ? <p className="field-error">{savePlacement.error.message}</p> : null}
                {clearPlacement.isError ? <p className="field-error">{clearPlacement.error.message}</p> : null}
              </div>
            ) : null}
            <button
              type="button"
              className="btn-outline btn-sm rd-block"
              disabled={!p || final.isPending}
              onClick={() => final.mutate()}
            >
              {final.isPending ? "ກຳລັງສ້າງ…" : "ເບິ່ງຕົວຢ່າງສຸດທ້າຍ (ລວມຕາປະທັບ)"}
            </button>
            {final.isError ? <p className="field-error">{final.error.message}</p> : null}
            <button type="button" className="btn-primary rd-block" disabled={!p} onClick={() => onSign(opts)}>
              <PenLine size={16} /> ລົງນາມ ແລະ ປະທັບຕາ
            </button>
            {canReturn ? (
              <button type="button" className="btn-outline rd-block" onClick={onReturn}>
                <X size={16} /> ສົ່ງຄືນ
              </button>
            ) : null}
          </div>
        </section>
        <section className="card" style={{ padding: "var(--space-4)" }}>
          <div className="row" style={{ justifyContent: "space-between" }}>
            <strong className="body-sm">ຄ່າທຳນຽມ</strong>
            <strong className="body-sm num">{row.paid_lak == null ? "ຍັງບໍ່ຊຳລະ" : fullLAK(row.paid_lak)}</strong>
          </div>
          <p className="caption">
            {row.paid_lak == null
              ? type?.fee_lak == null
                ? "ບໍ່ມີຄ່າທຳນຽມ"
                : `ອັດຕາ ${fullLAK(type.fee_lak)} / ສະບັບ`
              : `ຊຳລະແລ້ວຕອນຍື່ນ · ${num(row.copies ?? 1)} ສະບັບ`}
          </p>
        </section>
        <button type="button" className="btn-outline btn-sm rd-block" onClick={onOpenDetail}>
          <FileText size={14} /> ເບິ່ງລາຍລະອຽດຄຳຮ້ອງ
        </button>
      </div>

      {finalOpen && final.data ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ຕົວຢ່າງສຸດທ້າຍ">
          <div className="modal sd-final">
            <div className="modal__header">
              <h2 className="modal__title">ຕົວຢ່າງສຸດທ້າຍ — ຍັງບໍ່ໄດ້ລົງນາມ</h2>
              <button type="button" className="btn-outline" onClick={() => setFinalOpen(false)} aria-label="ປິດ">
                <X size={16} />
              </button>
            </div>
            <div className="sd-final__img">
              <img src={final.data} alt="ຕົວຢ່າງສຸດທ້າຍຂອງເອກະສານ" />
            </div>
            <div className="modal__actions">
              <button type="button" className="btn-secondary" onClick={() => setFinalOpen(false)}>
                ກັບໄປປັບຕຳແໜ່ງ
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setFinalOpen(false);
                  onSign(opts);
                }}
              >
                <PenLine size={16} /> ລົງນາມ ແລະ ປະທັບຕາ
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** ยืนยันลงนาม (ใบเดียวหรือหลายใบ) ด้วย PIN — เซ็นทีละใบตามลำดับ ใบที่ล้มเหลวยังอยู่ในคิว */
function SignConfirmModal({
  rows,
  opts,
  typeOf,
  onClose,
  onDone,
}: {
  rows: AdminRequestRow[];
  opts?: SignOptions;
  typeOf: (r: AdminRequestRow) => DocumentType | undefined;
  onClose: () => void;
  onDone: (signed: { id: string; number: string }[], failed: number) => void;
}) {
  const [pin, setPin] = useState("");
  const [counterparty, setCounterparty] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const single = rows.length === 1 ? rows[0] : null;
  const needsCounterparty = single ? isSaleContract(typeOf(single)) : false;

  const run = useMutation({
    mutationFn: async () => {
      const v = await api.POST("/v1/me/pin/verify", { body: { pin }, headers: authHeaders() });
      if (v.error) {
        throw new Error(
          v.response.status === 400
            ? "PIN ບໍ່ຖືກຕ້ອງ"
            : v.response.status === 429
              ? "ໃສ່ PIN ຜິດຫຼາຍເທື່ອ — ລໍຖ້າແລ້ວລອງໃໝ່"
              : describeError(v.error, v.response.status),
        );
      }
      const signed: { id: string; number: string }[] = [];
      let failed = 0;
      let lastError = "";
      for (const [i, r] of rows.entries()) {
        setProgress(`ກຳລັງລົງນາມ ${num(i + 1)} / ${num(rows.length)}…`);
        const { data, error, response } = await api.POST("/v1/admin/requests/{id}/sign", {
          params: { path: { id: r.id } },
          // ไม่ส่ง document_number = server จองเลขถัดไปเอง · ไม่ส่งกรอบ = กรอบลายเซ็น/QR ตั้งต้น
          body: {
            counterparty_confirmed: needsCounterparty ? counterparty : false,
            hide_signer_name: opts?.hide_signer_name ?? false,
            // ส่งเฉพาะตอนเลือกไม่ใส่ QR — API รุ่นก่อนมีช่องนี้ปฏิเสธฟิลด์ที่ไม่รู้จัก (400) · cast ดูเหตุผลข้างบน
            ...((opts?.hide_qr ? { hide_qr: true } : {}) as { hide_qr: boolean }),
            ...(opts?.signature_placement ? { signature_placement: opts.signature_placement } : {}),
            ...(opts?.qr_placement ? { qr_placement: opts.qr_placement } : {}),
          },
          headers: authHeaders(),
        });
        if (error || !data) {
          failed++;
          lastError = describeError(error, response.status);
        } else {
          signed.push({ id: r.id, number: data.document_number });
        }
      }
      if (signed.length === 0) throw new Error(lastError || "ລົງນາມບໍ່ສຳເລັດ");
      return { signed, failed };
    },
    onSuccess: ({ signed, failed }) => onDone(signed, failed),
    onSettled: () => setProgress(null),
  });

  const title = single ? "ຢືນຢັນລົງນາມ ແລະ ປະທັບຕາ" : `ລົງນາມ ${num(rows.length)} ລາຍການ`;
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <form
        className="modal modal--sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (pin.length === 6 && (!needsCounterparty || counterparty)) run.mutate();
        }}
      >
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ" disabled={run.isPending}>
            <X size={16} />
          </button>
        </div>
        <div className="stack">
          <div className="alert-error" role="note">
            <strong>ແກ້ໄຂບໍ່ໄດ້ຫຼັງລົງນາມ</strong> — ຖ້າຜິດ ຕ້ອງອອກສະບັບໃໝ່. ລະບົບໃສ່ລາຍເຊັນ ແລະ QR ກວດສອບ ພ້ອມອອກເລກທີ
            ແລະ ບັນທຶກຊື່ຜູ້ລົງນາມ.
          </div>
          {single ? (
            <dl className="rd-kv" style={{ marginTop: 0 }}>
              <dt>ເອກະສານ</dt>
              <dd>
                {typeOf(single)?.name_lo ?? "—"} ({typeOf(single)?.code})
              </dd>
              <dt>ຜູ້ຂໍ</dt>
              <dd>
                {single.requester_name || "—"}
                {single.village_name_lo ? ` · ບ້ານ${single.village_name_lo}` : ""}
              </dd>
            </dl>
          ) : (
            <div className="sd-confirm-list">
              {rows.map((r) => (
                <div key={r.id} className="row body-sm" style={{ justifyContent: "space-between" }}>
                  <span>{typeOf(r)?.name_lo ?? "—"}</span>
                  <span className="caption">{r.requester_name || "—"}</span>
                </div>
              ))}
            </div>
          )}
          {needsCounterparty ? (
            <label className="row body-sm" style={{ gap: "var(--space-2)", alignItems: "flex-start" }}>
              <input type="checkbox" checked={counterparty} onChange={(e) => setCounterparty(e.target.checked)} />
              <span>ອີກຝ່າຍມາເຊັນຢືນຢັນຕໍ່ໜ້າເຈົ້າໜ້າທີ່ແລ້ວ</span>
            </label>
          ) : null}
          <div>
            <label className="form-field-label" htmlFor="sign-pin">
              PIN 6 ຫຼັກ ຂອງທ່ານ <span className="required">*</span>
            </label>
            <input
              id="sign-pin"
              className="input-field num sd-pin"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              placeholder="••••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
          </div>
          {progress ? <p className="caption">{progress}</p> : null}
          {run.isError ? <div className="alert-error">{run.error.message}</div> : null}
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={run.isPending}>
            ຍົກເລີກ
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={pin.length < 6 || run.isPending || (needsCounterparty && !counterparty)}
          >
            <PenLine size={16} /> {single ? "ຢືນຢັນລົງນາມ" : `ຢືນຢັນລົງນາມ ${num(rows.length)} ລາຍການ`}
          </button>
        </div>
      </form>
    </div>
  );
}

/** ส่งคืนจากขั้นลงนาม (approved → needs_changes, ต้องมีเหตุผล) */
function ReturnModal({
  row,
  type,
  onClose,
  onDone,
}: {
  row: AdminRequestRow;
  type: DocumentType | undefined;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const send = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/v1/admin/requests/{id}/transitions", {
        params: { path: { id: row.id } },
        body: { to: "needs_changes", note: note.trim() },
        headers: authHeaders(),
      });
      if (error) throw new Error(describeError(error, response.status));
    },
    onSuccess: onDone,
  });
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ສົ່ງຄືນໃຫ້ແກ້ໄຂ">
      <div className="modal modal--sm">
        <div className="modal__header">
          <h2 className="modal__title">ສົ່ງຄືນໃຫ້ແກ້ໄຂ</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>
        <div className="stack">
          <p className="caption">
            {type?.name_lo ?? "—"} · {row.requester_name || "—"}
            {row.village_name_lo ? ` · ບ້ານ${row.village_name_lo}` : ""}
          </p>
          <label className="form-field-label" htmlFor="return-note">
            ເຫດຜົນ — ສົ່ງໃຫ້ຜູ້ຂໍທາງແອັບ <span className="required">*</span>
          </label>
          <div className="row" style={{ flexWrap: "wrap" }}>
            {RETURN_CHIPS.map((c) => (
              <button key={c} type="button" className="btn-outline btn-sm" onClick={() => setNote((n) => (n ? `${n}\n${c}` : c))}>
                {c}
              </button>
            ))}
          </div>
          <textarea
            id="return-note"
            className="input-field"
            rows={3}
            placeholder="ລະບຸຊ່ອງ ຫຼື ເອກະສານທີ່ຕ້ອງແກ້"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {send.isError ? <div className="alert-error">{send.error.message}</div> : null}
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={send.isPending}>
            ຍົກເລີກ
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={note.trim() === "" || send.isPending}
            onClick={() => send.mutate()}
          >
            ຢືນຢັນສົ່ງຄືນ
          </button>
        </div>
      </div>
    </div>
  );
}
